import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { createCustomerFromInvoice } from '../../store/slices/customerSlice';
import { Invoice, Customer } from '../../types/invoice';
import CustomerForm, { CustomerFormData } from './CustomerForm';

interface Props {
  open: boolean;
  invoice: Invoice;
  onClose: () => void;
  /** Called after successful save so the parent can refresh the invoice */
  onSaved: () => void;
}

const SaveToDirectoryDialog: React.FC<Props> = ({ open, invoice, onClose, onSaved }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicateCustomer, setDuplicateCustomer] = useState<Customer | null>(null);

  const handleClose = () => {
    setFormError(null);
    setDuplicateCustomer(null);
    onClose();
  };

  const handleSubmit = async (data: CustomerFormData) => {
    setFormError(null);
    setDuplicateCustomer(null);
    const result = await dispatch(
      createCustomerFromInvoice({ invoiceId: invoice.id, data })
    );
    if (createCustomerFromInvoice.fulfilled.match(result)) {
      onSaved();
      handleClose();
    } else {
      const payload = result.payload as any;
      setFormError(payload?.error || 'Nepodařilo se vytvořit dodavatele');
      setDuplicateCustomer(payload?.existing || null);
    }
  };

  // The directory tracks the suppliers (dodavatelé) who send you invoices.
  // Their data lives in the supplier_* fields of the invoice.
  // The supplier bank account is always what's printed on an incoming invoice.
  const buildBankAccount = (): string => {
    const acc = invoice.supplierBankAccount || '';
    // supplierBankAccount may already be "account/bankcode" combined
    if (!acc) return '';
    if (acc.includes('/')) return acc;
    // If there's a separate bank code field, combine
    const code = (invoice as any).supplierBankCode || '';
    if (code) return `${acc}/${code}`;
    return acc;
  };

  const initialData: Partial<CustomerFormData> = {
    name: invoice.supplierName || '',
    ico: invoice.supplierICO || '',
    dic: invoice.supplierDIC || '',
    address: invoice.supplierAddress || '',
    bankAccount: buildBankAccount(),
  };

  return (
    <CustomerForm
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      initialData={initialData}
      title="Uložit dodavatele do adresáře"
      submitLabel="Uložit do adresáře"
      errorMessage={formError}
      duplicateCustomer={duplicateCustomer}
    />
  );
};

export default SaveToDirectoryDialog;
